import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown, Trash2 } from 'lucide-react';
import type { Preticket, PreticketAcceptBody, PreticketStatus } from '@erp/shared';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input, Label, Select, Textarea } from '@/components/ui/input';
import { Badge, Dialog, EmptyState, Skeleton } from '@/components/ui/misc';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn, formatDateTime } from '@/lib/utils';
import { ClientPicker, type ClientRef } from '@/features/tickets/ClientPicker';
import { deskDetailQuery } from '@/features/tickets/tickets-queries';
import { preticketsListOptions } from './pretickets-queries';

const TAB_TRIGGER_CLASSES =
  'rounded-none border-b-2 border-transparent px-3 pb-2 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none';

const ERROR_LABELS: Record<string, string> = {
  preticket_already_decided: 'Este pré-ticket já foi aceito ou descartado.',
};

function errorMessage(err: unknown): string {
  if (err !== null && typeof err === 'object' && 'error' in err) {
    const code = (err as { error: unknown }).error;
    if (typeof code === 'string' && code.length > 0) return ERROR_LABELS[code] ?? code;
  }
  return err instanceof Error ? err.message : 'Erro inesperado';
}

/**
 * Triagem dos e-mails recebidos em mesas com entrada "Caixa de pré-tickets":
 * aceitar (vira ticket) ou descartar.
 *
 * v12: deixou de ser página/item de sidebar — é a view `?view=pretickets` de
 * /tickets (é triagem de ticket, não uma área). O contador de pendentes vive
 * no botão da view (TicketsPage); aqui ficam só as abas por status.
 */
export function PreticketsPanel() {
  const [tab, setTab] = useState<PreticketStatus>('pending');

  return (
    <Tabs value={tab} onValueChange={(v) => setTab(v as PreticketStatus)}>
      <div className="border-b">
        <TabsList className="h-auto gap-1 rounded-none bg-transparent p-0">
          <TabsTrigger value="pending" className={TAB_TRIGGER_CLASSES}>
            Pendentes
          </TabsTrigger>
          <TabsTrigger value="accepted" className={TAB_TRIGGER_CLASSES}>
            Aceitos
          </TabsTrigger>
          <TabsTrigger value="discarded" className={TAB_TRIGGER_CLASSES}>
            Descartados
          </TabsTrigger>
        </TabsList>
      </div>
      <TabsContent value="pending">
        <PreticketList status="pending" />
      </TabsContent>
      <TabsContent value="accepted">
        <PreticketList status="accepted" />
      </TabsContent>
      <TabsContent value="discarded">
        <PreticketList status="discarded" />
      </TabsContent>
    </Tabs>
  );
}

const EMPTY_MESSAGES: Record<PreticketStatus, string> = {
  pending: 'Nenhum pré-ticket pendente',
  accepted: 'Nenhum pré-ticket aceito',
  discarded: 'Nenhum pré-ticket descartado',
};

function PreticketList({ status }: { status: PreticketStatus }) {
  const queryClient = useQueryClient();
  const listQ = useQuery(preticketsListOptions(status));
  const [acceptTarget, setAcceptTarget] = useState<Preticket | null>(null);

  const discard = useMutation({
    mutationFn: async (id: number) => {
      const res = await api<Preticket>(`/pretickets/${id}/discard`, { method: 'POST' });
      if (res.error) throw res.error;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['erp', 'pretickets'] }),
  });

  if (listQ.isPending) {
    return (
      <div className="space-y-2 pt-2" aria-busy>
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-14 w-full" />
        ))}
      </div>
    );
  }
  if (listQ.isError) {
    return <EmptyState message={`Erro ao carregar pré-tickets: ${errorMessage(listQ.error)}`} />;
  }
  const items = listQ.data.items;
  if (items.length === 0) {
    return <EmptyState className="h-52" message={EMPTY_MESSAGES[status]} />;
  }

  return (
    <div className="pt-2">
      <ul className="space-y-2">
        {items.map((p) => (
          <PreticketRow
            key={p.id}
            preticket={p}
            onAccept={status === 'pending' ? () => setAcceptTarget(p) : undefined}
            onDiscard={
              status === 'pending'
                ? () => {
                    if (
                      window.confirm(
                        `Descartar o pré-ticket "${p.subject}" de ${p.fromEmail}? Ele não virará ticket.`
                      )
                    ) {
                      discard.mutate(p.id);
                    }
                  }
                : undefined
            }
            discardPending={discard.isPending}
          />
        ))}
      </ul>
      {discard.error ? (
        <p className="mt-2 text-sm text-destructive">{errorMessage(discard.error)}</p>
      ) : null}
      {acceptTarget ? (
        <AcceptDialog preticket={acceptTarget} onClose={() => setAcceptTarget(null)} />
      ) : null}
    </div>
  );
}

function PreticketRow({
  preticket,
  onAccept,
  onDiscard,
  discardPending,
}: {
  preticket: Preticket;
  onAccept?: () => void;
  onDiscard?: () => void;
  discardPending: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const from = preticket.fromName
    ? `${preticket.fromName} <${preticket.fromEmail}>`
    : preticket.fromEmail;

  return (
    <li className="rounded-md border border-divider text-sm transition-colors hover:border-border">
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-label={expanded ? 'Ocultar corpo do e-mail' : 'Ver corpo do e-mail'}
          className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <ChevronDown className={cn('size-4 transition-transform', expanded && 'rotate-180')} />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="truncate font-medium">{preticket.subject}</span>
            <Badge variant="primary">{preticket.desk.name}</Badge>
            {preticket.status === 'accepted' && preticket.ticketNumber !== null ? (
              <Link
                to="/tickets/$ticketNumber"
                params={{ ticketNumber: String(preticket.ticketNumber) }}
                className="font-medium text-primary-pressed hover:underline"
              >
                Ticket #{preticket.ticketNumber}
              </Link>
            ) : null}
            {preticket.status === 'discarded' ? <Badge>Descartado</Badge> : null}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
            <span className="truncate" title={from}>
              De: {from}
            </span>
            <span>·</span>
            <span className="whitespace-nowrap">Recebido em {formatDateTime(preticket.createdAt)}</span>
            {preticket.decidedAt !== null ? (
              <>
                <span>·</span>
                <span className="whitespace-nowrap">
                  Decidido em {formatDateTime(preticket.decidedAt)}
                </span>
              </>
            ) : null}
          </div>
        </div>
        {onAccept || onDiscard ? (
          <div className="flex shrink-0 items-center gap-1.5">
            {onAccept ? (
              <Button size="sm" onClick={onAccept}>
                <Check className="size-4" strokeWidth={2.5} />
                Aceitar
              </Button>
            ) : null}
            {onDiscard ? (
              <Button
                size="sm"
                variant="outline"
                disabled={discardPending}
                onClick={onDiscard}
                className="border-destructive/40 text-destructive hover:bg-destructive/5 hover:text-destructive"
              >
                <Trash2 className="size-3.5" />
                Descartar
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
      {expanded ? (
        <div className="border-t border-divider bg-muted/40 px-4 py-3">
          <p className="text-xs font-medium text-muted-foreground">Corpo do e-mail</p>
          <div className="mt-1 max-h-64 overflow-y-auto text-sm leading-relaxed whitespace-pre-wrap">
            {preticket.body.trim() || '—'}
          </div>
        </div>
      ) : null}
    </li>
  );
}

// ── Aceitar (vira ticket) ───────────────────────────────────────────────────

function AcceptDialog({ preticket, onClose }: { preticket: Preticket; onClose: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const deskQ = useQuery(deskDetailQuery(preticket.desk.id));
  const priorities = deskQ.data
    ? [...deskQ.data.priorities].sort((a, b) => a.position - b.position)
    : [];

  const [client, setClient] = useState<ClientRef | null>(null);
  const [title, setTitle] = useState(preticket.subject);
  const [description, setDescription] = useState(preticket.body);
  const [priorityId, setPriorityId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const accept = useMutation({
    mutationFn: async (body: PreticketAcceptBody) => {
      const res = await api<Preticket>(`/pretickets/${preticket.id}/accept`, {
        method: 'POST',
        body,
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: (updated) => {
      void queryClient.invalidateQueries({ queryKey: ['erp', 'pretickets'] });
      void queryClient.invalidateQueries({ queryKey: ['erp', 'tickets'] });
      onClose();
      if (updated?.ticketNumber != null) {
        void navigate({
          to: '/tickets/$ticketNumber',
          params: { ticketNumber: String(updated.ticketNumber) },
        });
      }
    },
    onError: (e) => setError(errorMessage(e)),
  });

  const submit = () => {
    setError(null);
    if (!client) {
      setError('Selecione o cliente.');
      return;
    }
    if (!title.trim()) {
      setError('Informe o título do ticket.');
      return;
    }
    accept.mutate({
      clientId: client.id,
      title: title.trim(),
      description: description.trim() ? description : null,
      priorityId: priorityId === '' ? null : Number(priorityId),
    });
  };

  return (
    <Dialog open onClose={onClose} title="Aceitar pré-ticket" className="sm:max-w-2xl">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <p className="text-sm text-muted-foreground">
          O e-mail de <span className="font-medium text-foreground">{preticket.fromEmail}</span>{' '}
          vira um ticket na mesa{' '}
          <span className="font-medium text-foreground">{preticket.desk.name}</span>, com o
          remetente como solicitante.
        </p>
        <div className="space-y-1.5">
          <Label>
            Cliente <span className="text-destructive">*</span>
          </Label>
          <ClientPicker selected={client} onSelect={setClient} />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="accept-title">
              Título <span className="text-destructive">*</span>
            </Label>
            <Input
              id="accept-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="accept-priority">Prioridade</Label>
            <Select
              id="accept-priority"
              value={priorityId}
              onChange={(e) => setPriorityId(e.target.value)}
              disabled={deskQ.isPending}
            >
              <option value="">Sem prioridade</option>
              {priorities.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="accept-description">Descrição</Label>
          <Textarea
            id="accept-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={6}
            className="min-h-32"
          />
        </div>
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={accept.isPending}>
            {accept.isPending ? 'Aceitando…' : 'Aceitar e criar ticket'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
